import React, { useState, memo, useCallback } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { setDialog } from "slices/appSlice";
import Button from "@mui/material/Button";
import QuarterYearModal from "./adoptionDecision/QuarterYearModal";
import AdoptionDecisionsModal from "./adoptionDecision/AdoptionDecisionsModal";
import Api from "api";

const CreateAdoptionDecisions = ({
  hpKey,
  gridData,
  refreshData,
  disabled,
}) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [quarter, setQuarter] = useState("");
  const [year, setYear] = useState("");
  const [policyModalOpen, setPolicyModalOpen] = useState(false);
  const [filteredPolicies, setFilteredPolicies] = useState([]);
  const [allLineOfBusinessDetailsList, setAllLineOfBusinessDetailsList] =
    useState([]);
  const [adoptionOptions, setAdoptionOptions] = useState([]);
  const [dialog, setDialog] = useState(null);

  const openQandYModal = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = () => {
    setOpen(false);
    setFilteredPolicies([]);
  };

  const openPolicyModal = useCallback(async () => {
    try {
      const [lobsResponse, adoptOptionsResponse] = await Promise.all([
        Api.policyEnforcementGrid.getAllLineOfBusinessConfig(hpKey),
        Api.policyEnforcementGrid.getAllAdoptionOptions(),
      ]);
      setAllLineOfBusinessDetailsList(lobsResponse.data);
      setAdoptionOptions(adoptOptionsResponse.data);
      setPolicyModalOpen(true);
    } catch (error) {
      setPolicyModalOpen(false);
      setDialog({ message: error.message, title: "Error" });
    }
  }, [hpKey]);

  const handleQuarterYearSubmit = useCallback(
    (selectedQuarter, selectedYear) => {
      // Update quarter and year in state
      setQuarter(selectedQuarter);
      setYear(selectedYear);
      const filteredPolicies = gridData
        .filter((row) => {
          return (
            row.coding.status === "Pending Decision" &&
            row.quarter === selectedQuarter &&
            row.year === selectedYear
          );
        })
        .sort((a, b) => {
          return a.policyNumber.localeCompare(b.policyNumber, undefined, {
            numeric: true,
            sensitivity: "base",
          });
        });
      if (filteredPolicies.length > 0) {
        setFilteredPolicies(filteredPolicies);
        openPolicyModal();
        setOpen(false);
      } else {
        setDialog({
          message: "No policies available for selected Quarter and Year",
        });
      }
    },
    [gridData, openPolicyModal]
  );

  const handleDialogClose = () => {
    setDialog(null);
  };

  return (
    <Box>
      {dialog && (
        <Dialog open onClose={handleDialogClose}>
          <DialogTitle>{dialog.title}</DialogTitle>
          <DialogContent>{dialog.message}</DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
      <Button disabled={hpKey == 999 || disabled} onClick={openQandYModal}>
        Enter Adoption Decisions
      </Button>

      {/** First Modal **/}
      {open && (
        <QuarterYearModal
          handleClose={handleClose}
          gridData={gridData}
          onSubmit={handleQuarterYearSubmit}
        />
      )}

      {/** Second Modal **/}
      {policyModalOpen && (
        <AdoptionDecisionsModal
          quarter={quarter}
          year={year}
          filteredPolicies={filteredPolicies}
          adoptionOptions={adoptionOptions}
          allLineOfBusinessDetailsList={allLineOfBusinessDetailsList}
          handleClose={() => setPolicyModalOpen(false)}
          onSuccess={({ message, savedPolicyKeys }) => {
            setPolicyModalOpen(false); // close child first
            refreshData();
            setDialog({ title: "Success", message: message });
          }}
        />
      )}
    </Box>
  );
};

// Memoize the component
const MemoizedCreateAdoptionDecisions = memo(CreateAdoptionDecisions);
MemoizedCreateAdoptionDecisions.displayName = "MemoizedCreateAdoptionDecisions";

export default MemoizedCreateAdoptionDecisions;
